import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from '../../../store';
import apiClient from '../../shared/auth.interceptor';
import { doLogin, userRefresh } from '../../shared/auth.functions';
import { errorToast } from '../../shared/toast.service';
import Loader from '../../shared/Loader/loader';
import { getPasswordErrorMessages, passwordValidator } from '../../validate';

const MAX_NAME_LENGTH = 25;

const apiError = (error: any, fallback: string) => {
  const message = error?.response?.data?.message;
  return Array.isArray(message) ? message.join(', ') : message || fallback;
};

// The app's inputs are white with no border; outline them so they show on the white page.
const fieldSx = {
  '& .MuiOutlinedInput-root fieldset': { border: '1px solid #d0d5dd' },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#98a2b3' }
};

const nameError = (value: string) => {
  const v = value.trim();
  if (!v) return 'Please enter a name';
  if (v.length > MAX_NAME_LENGTH) return `Use ${MAX_NAME_LENGTH} characters or fewer`;
  return '';
};

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.currentUser.currentUser);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Reload so the sign-in details are current.
  useEffect(() => {
    userRefresh(dispatch);
  }, [dispatch]);
  useEffect(() => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
  }, [user?.firstName, user?.lastName]);

  if (!user) return <Loader />;

  const hasPassword = user.hasPassword !== false;
  const nameChanged = firstName.trim() !== (user.firstName || '') || lastName.trim() !== (user.lastName || '');
  const nameInvalid = !!nameError(firstName) || !!nameError(lastName);
  const passwordProblem =
    newPassword && getPasswordErrorMessages(passwordValidator(newPassword))
      ? getPasswordErrorMessages(passwordValidator(newPassword))
      : confirmPassword && confirmPassword !== newPassword
        ? 'Passwords do not match'
        : '';
  const canSavePassword =
    !!newPassword && newPassword === confirmPassword && !passwordProblem && (!hasPassword || !!currentPassword);

  const saveName = async () => {
    setSavingName(true);
    try {
      await apiClient.patch(`/v1/user/${user._id}`, { firstName: firstName.trim(), lastName: lastName.trim() });
      await userRefresh(dispatch);
      toast.success('Name saved');
    } catch (error) {
      errorToast(apiError(error, 'Could not save your name'));
    } finally {
      setSavingName(false);
    }
  };

  const resetName = () => {
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
  };
  const resetPassword = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };
  const passwordTyped = !!(currentPassword || newPassword || confirmPassword);
  // Back to where they came from, or Clients when Profile was opened directly.
  const close = () => (window.history.length > 1 ? navigate(-1) : navigate('/user/clients'));

  const savePassword = async () => {
    setSavingPassword(true);
    try {
      const { data } = await apiClient.put('/v1/user/password', {
        ...(hasPassword ? { currentPassword } : {}),
        newPassword
      });
      // The API starts a new session, which signs out your other devices.
      doLogin(data);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      await userRefresh(dispatch);
      toast.success(hasPassword ? 'Password changed' : 'Password set');
    } catch (error) {
      errorToast(apiError(error, 'Could not save your password'));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Box className="d-flex align-items-center justify-content-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Profile</Typography>
        <IconButton aria-label="Close profile" onClick={close}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1.5 }}>
        Your details
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="First name"
          size="small"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          error={!!nameError(firstName)}
          helperText={nameError(firstName) || undefined}
          inputProps={{ maxLength: MAX_NAME_LENGTH }}
          sx={{ flex: 1, minWidth: 200, ...fieldSx }}
        />
        <TextField
          label="Last name"
          size="small"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          error={!!nameError(lastName)}
          helperText={nameError(lastName) || undefined}
          inputProps={{ maxLength: MAX_NAME_LENGTH }}
          sx={{ flex: 1, minWidth: 200, ...fieldSx }}
        />
      </Box>
      <TextField label="Email" size="small" value={user.email} disabled fullWidth sx={{ mt: 1.5, ...fieldSx }} />
      <Box className="d-flex justify-content-end mt-2" sx={{ gap: 1 }}>
        <Button variant="outlined" color="secondary" onClick={resetName} disabled={!nameChanged || savingName}>
          Cancel
        </Button>
        <Button variant="contained" onClick={saveName} disabled={!nameChanged || nameInvalid || savingName}>
          {savingName ? 'Saving…' : 'Save name'}
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        Sign in with Intuit
      </Typography>
      <Box className="d-flex align-items-center mt-1" sx={{ gap: 1.5 }}>
        <Chip
          label={user.intuitLinked ? 'Linked' : 'Not linked'}
          color={user.intuitLinked ? 'success' : 'default'}
          size="small"
        />
        <Typography variant="body2" color="textSecondary">
          {user.intuitLinked
            ? 'You can sign in with your Intuit account as well as your email.'
            : 'Use Sign in with Intuit on the Login page to link your Intuit account.'}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {hasPassword ? 'Change password' : 'Set a password'}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
        {hasPassword
          ? 'Changing your password signs you out on your other devices.'
          : 'You sign in with Intuit. Set a password to also sign in with your email.'}
      </Typography>
      {hasPassword && (
        <TextField
          label="Current password"
          type="password"
          size="small"
          fullWidth
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          sx={{ mb: 1.5, ...fieldSx }}
        />
      )}
      <TextField
        label="New password"
        type="password"
        size="small"
        fullWidth
        autoComplete="new-password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        helperText="8 or more characters, with upper and lower case letters, a number and a symbol (!@#$%^&*)"
        sx={{ mb: 1.5, ...fieldSx }}
      />
      <TextField
        label="Confirm new password"
        type="password"
        size="small"
        fullWidth
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={!!passwordProblem}
        helperText={passwordProblem || undefined}
        FormHelperTextProps={{ sx: { whiteSpace: 'pre-line' } }}
        sx={fieldSx}
      />
      <Box className="d-flex justify-content-end mt-2" sx={{ gap: 1 }}>
        <Button variant="outlined" color="secondary" onClick={resetPassword} disabled={!passwordTyped || savingPassword}>
          Cancel
        </Button>
        <Button variant="contained" onClick={savePassword} disabled={!canSavePassword || savingPassword}>
          {savingPassword ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
        </Button>
      </Box>
    </Box>
  );
};

export default Profile;
